import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import type {
  GridColDef,
  GridRowId,
  GridValidRowModel,
  GridAggregationFunction,
} from '@mui/x-data-grid-premium';
import {
  gridColumnDefinitionsSelector,
  gridFilteredSortedRowEntriesSelector,
} from '@mui/x-data-grid-premium';
import { localeFormatterHelper } from '../../../helpers/formatter/localeFormatter.helper';

export const handleExportDocument = (
  visibleApiRef: { current: GridApiPremium } | null,
  options?: {
    rowsPerPage?: number;
    title?: string;
    aggregationFunctions?: Record<string, GridAggregationFunction<any, any>>;
    aggregationPosition?: 'footer' | 'none';
  },
) => {
  if (!visibleApiRef) return;

  printGridStateAsHtmlTable({
    apiRef: visibleApiRef,
    rowsPerPage: options?.rowsPerPage ?? 18,
    title: options?.title,
    aggregationFunctions: options?.aggregationFunctions,
    aggregationPosition: options?.aggregationPosition ?? 'footer',
  });
};

export const getGroupingModel = (api: GridApiPremium): string[] => {
  const m1 = api?.state?.rowGrouping?.model;
  if (Array.isArray(m1)) return m1;
  return [];
};

const printGridStateAsHtmlTable = (params: {
  apiRef: { current: GridApiPremium };
  rowsPerPage?: number;
  title?: string;
  aggregationFunctions?: Record<string, GridAggregationFunction<any, any>>;
  aggregationPosition?: 'footer' | 'none';
}) => {
  const {
    apiRef,
    rowsPerPage = 30,
    title,
    aggregationFunctions,
    aggregationPosition = 'footer',
  } = params;

  const api = apiRef.current;

  const allColumns = gridColumnDefinitionsSelector(apiRef) as GridColDef[];

  const headerByField = new Map<string, string>();
  allColumns.forEach((c) => {
    headerByField.set(c.field, c.headerName ?? c.field);
  });

  const visibilityModel =
    (api?.state?.columns?.columnVisibilityModel as Record<string, boolean | undefined>) ?? {};

  const printColumns = allColumns.filter((c) => {
    if (c.disableExport) return false;
    const isVisible = visibilityModel[c.field] !== false;
    return isVisible;
  });

  const raw = gridFilteredSortedRowEntriesSelector(apiRef) as unknown;

  const rowEntries: RowEntry[] = Array.isArray(raw)
    ? (raw as unknown[]).map((e) => {
        if (Array.isArray(e) && e.length >= 2) {
          return [e[0] as GridRowId, e[1] as GridValidRowModel];
        }
        const obj = e as { id: GridRowId; model: GridValidRowModel };
        return [obj.id, obj.model];
      })
    : [];

  const exported = api.exportState() as any;
  const aggregationModel: Record<string, string> =
    exported?.aggregation?.model ?? (api as any)?.state?.aggregation?.model ?? {};

  const leafRowIds: GridRowId[] = rowEntries
    .filter(([id]) => !isGroupRowId(id, api))
    .map(([id]) => id);

  const thead = `
    <thead>
      <tr>
        ${printColumns
          .map((c) => {
            const align = getColHeaderAlign(c);
            return `<th style="text-align:${align};">${escapeHtml(c.headerName ?? c.field)}</th>`;
          })
          .join('')}
      </tr>
    </thead>
  `;

  const tbody = rowEntries
    .map(([id], idx) => {
      const rowIndex1 = idx + 1;
      const pageBreakClass = rowIndex1 % rowsPerPage === 0 ? 'page-break' : '';

      if (isGroupRowId(id, api)) {
        const indent = getGroupIndentPx(id, api);
        return `
          <tr class="group-row ${pageBreakClass}">
            <td colspan="${printColumns.length}">
              <span style="padding-left:${indent}px">${getGroupLabelFromTree(
          id,
          headerByField,
          api,
        )}</span>
            </td>
          </tr>
        `;
      }

      return `
        <tr class="${pageBreakClass}">
          ${printColumns
            .map((c) => {
              const align = getColCellAlign(c);
              return `<td style="text-align:${align};">${getCellText(c, id, api)}</td>`;
            })
            .join('')}
        </tr>
      `;
    })
    .join('');

  const tfoot =
    aggregationPosition === 'footer' && Object.keys(aggregationModel).length > 0
      ? `
    <tfoot>
      <tr class="agg-row">
        ${printColumns
          .map((c) => {
            const align = getColCellAlign(c);
            return `<td style="text-align:${align};">${computeFooterValue(
              c.field,
              aggregationModel,
              leafRowIds,
              api,
              aggregationFunctions,
            )}</td>`;
          })
          .join('')}
      </tr>
    </tfoot>
  `
      : '';

  const html = `
    <!doctype html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>${escapeHtml(title ?? 'Print')}</title>
      <style>
        @page { size: landscape; margin: 1cm 0.5cm 0.5cm 0.5cm; }

        html, body {
          font-family: Verdana, Arial, sans-serif;
          font-size: 7pt;
          color: #000;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          border: 1px solid #000;
        }

        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }

        th, td {
          border-right: 1px solid #000;
          border-bottom: 1px solid #000;
          padding: 0 6px;
          height: 24px;
          line-height: 24px;
          vertical-align: middle;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        th { font-weight: 700; background: transparent; }
        tr.group-row td { font-weight: 700; }
        tr.agg-row td { font-weight: 700; }

        tr.page-break { break-after: page; page-break-after: always; }
        tr { break-inside: avoid; page-break-inside: avoid; }
      </style>
    </head>
    <body>
      ${title ? `<div style="font-weight:700; margin-bottom:8px;">${escapeHtml(title)}</div>` : ''}
      <table>
        ${thead}
        <tbody>${tbody}</tbody>
        ${tfoot}
      </table>
    </body>
    </html>
  `;

  printHtmlInHiddenIframe(html);
};

const escapeHtml = (v: unknown): string =>
  safeToString(v)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const safeToString = (value: unknown): string => {
  if (value == null) return '';

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value
        .map((v) => safeToString(v))
        .filter(Boolean)
        .join(', ');
    }
    return '';
  }

  return String(value);
};

const stripHtmlTags = (value: unknown): string => {
  const s = safeToString(value);

  if (!s.includes('<')) {
    return escapeHtml(s);
  }

  const temp = document.createElement('div');
  temp.innerHTML = s;

  const text = temp.textContent || temp.innerText || '';
  return escapeHtml(text);
};

const getTreeNode = (rowId: GridRowId, api: GridApiPremium) => {
  const tree: Record<string, any> | undefined = (api as any)?.state?.rows?.tree;
  return tree?.[String(rowId)];
};

const isGroupRowId = (rowId: GridRowId, api: GridApiPremium): boolean => {
  const node = getTreeNode(rowId, api);
  return node?.type === 'group';
};

const getGroupLabelFromTree = (
  rowId: GridRowId,
  headerByField: Map<string, string>,
  api: GridApiPremium,
): string => {
  const node = getTreeNode(rowId, api);
  if (!node) return 'Group';

  const field: string | undefined = node.groupingField;
  const key = node.groupingKey;
  const displayField = field != null ? headerByField.get(field) ?? field : undefined;

  const directChildren = Array.isArray(node.children) ? node.children.length : undefined;
  const descendantCount =
    typeof node.descendantCount === 'number' ? node.descendantCount : directChildren;

  const base = stripHtmlTags(
    displayField != null && key != null
      ? `${displayField}: ${safeToString(key)}`
      : safeToString(key ?? 'Group'),
  );

  return descendantCount != null ? `${base} (${descendantCount})` : base;
};

const getGroupIndentPx = (rowId: GridRowId, api: GridApiPremium): number => {
  const node = getTreeNode(rowId, api);
  const depth = typeof node?.depth === 'number' ? node.depth : 0;
  return depth * 16;
};

const getCellText = (col: GridColDef, rowId: GridRowId, api: GridApiPremium): string => {
  try {
    const cellParams = api.getCellParams(rowId, col.field) as any;
    const v = cellParams.formattedValue ?? cellParams.value;
    return stripHtmlTags(v);
  } catch {
    return '';
  }
};

const computeFooterValue = (
  field: string,
  aggregationModel: Record<string, string>,
  leafRowIds: GridRowId[],
  api: GridApiPremium,
  aggregationFunctions?: Record<string, GridAggregationFunction<any, any>>,
): string => {
  const fnName = aggregationModel[field];
  if (!fnName) return '';

  const fn = aggregationFunctions?.[fnName];
  if (!fn?.apply) return '';

  const values = leafRowIds.map((rowId) => {
    try {
      const p = api.getCellParams(rowId, field) as any;
      return p?.value;
    } catch {
      return undefined;
    }
  });

  const res = fn.apply({
    values,
    groupId: null,
    field,
  } as any);

  if (res == null) return '';

  let formatted: unknown = res;

  // Apply number formatting only for numeric results
  if (typeof res === 'number' && !isNaN(res)) {
    formatted = localeFormatterHelper.formatNumber(res);
  }

  return stripHtmlTags(formatted);
};

const toCssAlign = (a: GridColDef['align'] | GridColDef['headerAlign'] | undefined): string => {
  if (a === 'right') return 'right';
  if (a === 'center') return 'center';
  return 'left';
};

const getColCellAlign = (col: GridColDef): string => {
  if (col.align) return toCssAlign(col.align);
  if (col.type === 'number') return 'right';
  return 'left';
};

const getColHeaderAlign = (col: GridColDef): string => {
  if (col.headerAlign) return toCssAlign(col.headerAlign);
  return getColCellAlign(col);
};

const printHtmlInHiddenIframe = (html: string) => {
  const PRINT_DELAY_MS = 50;
  const FALLBACK_PRINT_MS = 800;
  const FALLBACK_CLEANUP_MS = 1500;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';

  iframe.srcdoc = html;
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  if (!win) {
    iframe.remove();
    return;
  }

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    iframe.remove();
  };

  let printed = false;
  const doPrintOnce = () => {
    if (printed) return;
    printed = true;

    try {
      win.focus();
      win.print();
    } finally {
      setTimeout(cleanup, FALLBACK_CLEANUP_MS);
    }
  };

  win.onafterprint = () => setTimeout(cleanup, 0);

  iframe.addEventListener(
    'load',
    () => {
      setTimeout(doPrintOnce, PRINT_DELAY_MS);
    },
    { once: true },
  );

  setTimeout(() => {
    doPrintOnce();
  }, FALLBACK_PRINT_MS);
};

type RowEntry = [GridRowId, GridValidRowModel];
